import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useSEO } from "@/lib/useSEO";

/**
 * Real 404 page — noindex so Google doesn't index broken URLs as duplicates
 * of the landing page, and offers the user a way back to the meaningful
 * routes. Uses the same editorial dark shell as every other page.
 */
export default function NotFound() {
  useSEO({
    title: "Page not found · My Floor Studio",
    description:
      "The page you followed doesn't exist. Return to My Floor Studio — Timber, Carpet, Tile and Stone under one roof in Drummoyne, Sydney.",
    path: "/404",
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-[#121315] grain flex flex-col">
      <Nav />
      <main
        role="main"
        className="flex-1 flex items-center justify-center px-6 md:px-12 py-32 md:py-40"
      >
        <div className="max-w-2xl text-center">
          <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-8">
            404 · Off the map
          </p>
          <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl sm:text-6xl md:text-7xl leading-[0.98]">
            This page has drifted.
          </h1>
          <p className="mt-8 text-[#D6D1CA]/80 text-lg font-body leading-relaxed max-w-lg mx-auto">
            The URL you followed no longer points anywhere in the studio.
            Everything else is right here — pick a route below or return to
            the landing.
          </p>

          <div className="mt-14 flex flex-wrap gap-3 justify-center">
            {[
              { to: "/", label: "Landing" },
              { to: "/collection", label: "The Collection" },
              { to: "/visualise", label: "Visualise a Room" },
              { to: "/archive", label: "The Archive" },
              { to: "/about", label: "About the Studio" },
              { to: "/contact", label: "Contact" },
            ].map((r) => (
              <Link
                key={r.to}
                to={r.to}
                className="inline-block rounded-full border border-[#8C7355]/40 px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase text-[#D6D1CA]/85 hover:border-[#C9A876] hover:text-[#F5F4F0] transition-colors"
              >
                {r.label}
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
